import React, { useState } from 'react';
import {AppBar, Box, Toolbar, Typography, Button, IconButton, Menu, MenuItem, Avatar, Drawer, List, ListItem, ListItemText, useMediaQuery, useTheme, Container, Tooltip} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Map as MapIcon,
} from '@mui/icons-material';
import AnalyticsIcon from '@mui/icons-material/Insights';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/firebase';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // State for mobile drawer
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // State for user menu
  const [anchorEl, setAnchorEl] = useState(null);
  
  const { currentUser, isAuthenticated } = useAuth();
  
  // Toggle mobile drawer
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  // Handle menu open/close
  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  // Close menu
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  // Handle navigation
  const handleNavigate = (path) => {
    navigate(path);
    handleClose();
    setMobileOpen(false);
  };
  
  // Handle logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('isAuthenticated'); 
      handleClose();
      navigate('/');
    } catch (error) {
      console.error('Error signing out: ', error);
    }
  };

  // Navigation items for mobile drawer (all)
  const mobileNavigation = [
    { name: 'Monitor Map', path: '/monitoring', icon: <MapIcon fontSize="small" sx={{ mr: 0.5 }} /> },
    { name: 'Analysis', path: '/analysis', icon: <AnalyticsIcon fontSize="small" sx={{ mr: 0.5 }} /> },
    // { name: 'Upload Image', path: '/upload', icon: <UploadIcon fontSize="small" sx={{ mr: 0.5 }} /> }
    // { name: 'Profile', path: isAuthenticated ? '/profile' : '/signin', icon: <AccountCircle fontSize="small" sx={{ mr: 0.5 }} /> }
  ];

  // Navigation items for desktop center (only Monitor Map)
  const desktopNavigation = [
    { name: 'Monitor Map', path: '/monitoring', icon: <MapIcon fontSize="small" sx={{ mr: 0.5 }} /> },
    { name: 'Analysis', path: '/analysis', icon: <AnalyticsIcon fontSize="small" sx={{ mr: 0.5 }} /> },
  ];

  // Drawer for mobile view
  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Typography variant="h6" sx={{ my: 2, fontWeight: 'bold' }}>
        ENVIMON
      </Typography>
      <List>
        {mobileNavigation.map((item) => (
          <ListItem
            button
            key={item.name}
            component={Link}
            to={item.path}
            selected={location.pathname === item.path}
            sx={{
              color: 'inherit',
              '&.Mui-selected': {
                bgcolor: 'rgba(46, 125, 50, 0.1)',
                color: '#2e7d32',
              },
            }}
          >
            {item.icon}
            <ListItemText primary={item.name} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <AppBar position="sticky" sx={{ bgcolor: 'white', color: 'black', boxShadow: 1 }}>
      <Container maxWidth="xl">
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
          {/* Left section with logo only */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            
            {/* Logo */}
            <Typography
              variant="h6"
              component={Link}
              to="/"
              sx={{
                fontWeight: 'bold',
                color: 'inherit',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              ENVIMON
            </Typography>
          </Box>
          
          {/* Center section with navigation links */}
          {!isMobile && (
            <Box sx={{ 
              position: 'absolute', 
              left: '50%', 
              transform: 'translateX(-50%)',
              display: 'flex'
            }}>
              {desktopNavigation.map((item) => (
                <Button
                  key={item.name}
                  component={Link}
                  to={item.path}
                  sx={{
                    color: location.pathname === item.path ? '#2e7d32' : 'inherit',
                    mx: 1,
                    textTransform: 'none',
                    fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                  }}
                  startIcon={item.icon}
                >
                  {item.name}
                </Button>
              ))}
            </Box>
          )}
          
          {/* Right section with profile button*/}
          <Box>
            {isMobile ? (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={handleDrawerToggle}
                sx={{ ml: 1 }}
                edge="end"
              >
                <MenuIcon />
              </IconButton>
            ) : (
              <>
                {/* <Tooltip title={isAuthenticated ? "Account" : "Sign in"}>
                  <IconButton
                    size="large"
                    aria-label={isAuthenticated ? "account menu" : "sign in"}
                    onClick={isAuthenticated ? handleMenu : () => navigate('/signin')}
                    color="inherit"
                  >
                    {isAuthenticated ? (
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#2e7d32' }}>
                        {currentUser?.email?.charAt(0)?.toUpperCase() || 'U'}
                      </Avatar>
                    ) : (
                      <AccountCircle sx={{ color: '#2e7d32' }} />
                    )}
                  </IconButton>
                </Tooltip> */}

                {isAuthenticated && (
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                  >
                    {/* <MenuItem onClick={() => handleNavigate('/profile')}>Profile</MenuItem> */}
                    <MenuItem onClick={handleLogout}>Sign out</MenuItem>
                  </Menu>
                )}
              </>
            )}
          </Box>
        </Toolbar>
      </Container>
      
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        anchor='right'
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>
      
    </AppBar>
  );
};

export default Navbar;